<script lang="ts" setup>
import type { CollectionFieldPath, Filter, PrimitiveFieldPath } from '#layers/data-model/public';
import type { FilterSchemaCollection } from './utils/filter-schema';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CollectionFieldPathSelect } from '#layers/data-model/public';
import { useRelatedItemFilter } from '#layers/filters/composables/use-related-item-filter';
import { isPrimitiveFieldPath } from './utils/filter-path';
import { injectFilterSchemaContext } from './utils/filter-schema-context';

const props = withDefaults(defineProps<{
	collection: FilterSchemaCollection;
	includeRelationFields?: boolean;
	includeGroupAction?: boolean;
	disabled?: boolean;
}>(), {
	includeRelationFields: true,
	includeGroupAction: true,
});

const emit = defineEmits<{
	addGroup: [];
	addFilter: [fieldPath: PrimitiveFieldPath];
	addCondition: [condition: Filter];
}>();

const { t } = useI18n();
const { findFieldById, findOppositeCollectionByRelationFieldId } = injectFilterSchemaContext();
const { buildRelatedItemCondition } = useRelatedItemFilter();
const includeSelectableRelationFields = computed(() => props.includeRelationFields !== false);

const ADD_GROUP_ACTION_ID = 'add-group';

const leadingActions = computed(() => props.includeGroupAction
	? [{
			id: ADD_GROUP_ACTION_ID,
			label: t('filters.filter_group'),
			icon: 'lucide:plus',
		}]
	: [],
);

const availableFilters = computed(() => [
	...props.collection.primitiveFields,
	...(props.collection.relationFields ?? []).filter((field) => findOppositeCollectionByRelationFieldId(field.id)),
]);

// A primitive selection adds a plain field filter; selecting a relation itself opens the item
// picker and adds a related-item match for that relation path.
async function onFieldSelect(fieldPath: CollectionFieldPath) {
	if (isPrimitiveFieldPath(fieldPath, { findFieldById })) {
		emit('addFilter', fieldPath);
		return;
	}

	const condition = await buildRelatedItemCondition(fieldPath);

	if (condition) emit('addCondition', condition);
}

function onAction(actionId: string) {
	if (props.includeGroupAction && actionId === ADD_GROUP_ACTION_ID) {
		emit('addGroup');
	}
}
</script>

<template>
	<CollectionFieldPathSelect
		:collection
		include-constrained-fields
		:include-relation-fields="includeSelectableRelationFields"
		:include-list-relation-fields="includeSelectableRelationFields"
		terminal="primitive-or-relation"
		order="schema"
		size="md"
		:resolve-relation-collection="findOppositeCollectionByRelationFieldId"
		:leading-actions
		:search-placeholder="$t('filters.filter_search_placeholder')"
		@action="onAction"
		@select="onFieldSelect"
	>
		<template #default="{ open }">
			<slot
				:available-filters
				:open
			>
				<DButton
					color="neutral"
					variant="ghost"
					icon="lucide:list-filter"
					:aria-label="$t('filters.filter')"
					:label="$t('filters.add_filter')"
					size="sm"
					:disabled
				/>
			</slot>
		</template>
	</CollectionFieldPathSelect>
</template>
